import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Search, User, ShoppingBag, Menu, X, Heart } from "lucide-react";
import { useCart } from "@/context/CartContext";

const NAV = [
    { label: "Home", to: "/" },
    { label: "Shop", to: "/shop" },
    { label: "Best Sellers", to: "/best-sellers" },
    { label: "About Us", to: "/about" },
    { label: "Contact", to: "/contact" },
];

const ANNOUNCE = "FAITH. FASHION. STRENGTH. WEAR YOUR BLESSINGS.";
const LOGO_SRC =
    "https://customer-assets.emergentagent.com/job_faith-fashion-gift/artifacts/tc9npf4t_IMG_8992.png";

function Logo({ className = "", imgClassName = "h-32 sm:h-36 lg:h-44" }) {
    return (
        <Link
            to="/"
            data-testid="brand-logo"
            aria-label="Beyond Blessed Gifts & More LLC home"
            className={`inline-block group ${className}`}
        >
            <img
                src={LOGO_SRC}
                alt="Beyond Blessed Gifts & More LLC logo"
                className={`${imgClassName} w-auto transition-transform duration-700 group-hover:scale-105 group-hover:drop-shadow-[0_0_22px_rgba(217,138,154,0.55)]`}
                draggable="false"
            />
        </Link>
    );
}

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { itemCount, setOpen: setCartOpen } = useCart();

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <header className="sticky top-0 z-50" data-testid="site-header">
            {/* Announcement bar */}
            <div
                className="bg-bb-blush text-bb-warm-brown overflow-hidden"
                data-testid="announcement-bar"
            >
                <div className="py-2 whitespace-nowrap">
                    <div className="inline-flex announce-track">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <span
                                key={i}
                                className="inline-flex items-center gap-3 px-6 text-[0.7rem] sm:text-xs tracking-[0.32em] font-medium"
                            >
                                <Heart size={11} className="fill-current animate-heart-beat" />
                                {ANNOUNCE}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main nav */}
            <nav className="bg-white border-b border-bb-warm-brown/20 relative" data-testid="main-nav">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-28 sm:h-36 lg:h-44 flex items-center justify-between gap-4">
                    {/* Oversized transparent logo on white nav */}
                    <div className="flex-shrink-0 flex items-center">
                        <Logo />
                    </div>

                    {/* Desktop links */}
                    <ul className="hidden lg:flex items-center gap-8 xl:gap-10">
                        {NAV.map((item) => (
                            <li key={item.label}>
                                <NavLink
                                    to={item.to}
                                    end={item.to === "/"}
                                    data-testid={`nav-link-${item.label
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `relative font-body text-[0.78rem] tracking-[0.22em] uppercase transition-colors ${
                                            isActive
                                                ? "text-bb-pink-script"
                                                : "text-bb-text hover:text-bb-pink-script"
                                        }`
                                    }
                                >
                                    {({ isActive }) => (
                                        <span className="inline-block">
                                            {item.label}
                                            <span
                                                className={`block h-[1px] mt-1 transition-all duration-500 ${
                                                    isActive
                                                        ? "w-full bg-bb-pink-script"
                                                        : "w-0 bg-bb-pink-script"
                                                }`}
                                            />
                                        </span>
                                    )}
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    {/* Icons */}
                    <div className="flex items-center gap-4 sm:gap-5">
                        <button
                            data-testid="icon-search"
                            aria-label="Search"
                            className="text-bb-text icon-hover hover:text-bb-pink-script"
                        >
                            <Search size={20} />
                        </button>
                        <button
                            data-testid="icon-account"
                            aria-label="Account"
                            className="text-bb-text icon-hover hover:text-bb-pink-script"
                        >
                            <User size={20} />
                        </button>
                        <button
                            data-testid="icon-cart"
                            aria-label="Cart"
                            onClick={() => setCartOpen(true)}
                            className="relative text-bb-text icon-hover hover:text-bb-pink-script"
                        >
                            <ShoppingBag size={20} />
                            <span
                                className={`absolute -top-2 -right-2 text-[0.6rem] bg-bb-pink-script text-white rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 font-semibold ${
                                    itemCount > 0 ? "animate-soft-pulse" : "opacity-70"
                                }`}
                                data-testid="cart-count"
                            >
                                {itemCount}
                            </span>
                        </button>
                        <button
                            data-testid="mobile-menu-toggle"
                            aria-label="Toggle menu"
                            className="lg:hidden text-bb-text ml-1 icon-hover"
                            onClick={() => setOpen(true)}
                        >
                            <Menu size={24} />
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile menu */}
            <div
                className={`fixed inset-0 z-[60] lg:hidden ${
                    open ? "pointer-events-auto" : "pointer-events-none"
                }`}
                aria-hidden={!open}
            >
                <div
                    className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
                        open ? "opacity-100" : "opacity-0"
                    }`}
                    onClick={() => setOpen(false)}
                />
                <aside
                    className={`mobile-menu absolute right-0 top-0 h-full w-[82%] max-w-sm bg-white text-bb-text shadow-2xl ${
                        open ? "open" : ""
                    }`}
                    data-testid="mobile-menu"
                >
                    <div className="flex items-center justify-between px-6 h-24 border-b border-bb-warm-brown/20">
                        <Logo imgClassName="h-16" />
                        <button
                            data-testid="mobile-menu-close"
                            aria-label="Close menu"
                            onClick={() => setOpen(false)}
                            className="text-bb-text hover:text-bb-pink-script transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>
                    <ul className="px-6 py-6 flex flex-col gap-1">
                        {NAV.map((item, i) => (
                            <li
                                key={item.label}
                                className="opacity-0 animate-slide-in-right"
                                style={{
                                    animationDelay: open ? `${i * 70}ms` : "0ms",
                                    animationFillMode: "forwards",
                                }}
                            >
                                <NavLink
                                    to={item.to}
                                    end={item.to === "/"}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-${item.label
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `block py-3 border-b border-bb-warm-brown/15 font-serif-display text-lg tracking-wide ${
                                            isActive
                                                ? "text-bb-pink-script"
                                                : "text-bb-text"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                    <div className="px-6 mt-6 text-xs text-bb-pink-script tracking-[0.25em] uppercase">
                        Pass Christian, MS
                    </div>
                </aside>
            </div>
        </header>
    );
}
